import {
  Button,
  DrylusProvider,
  FormGroup,
  Icon,
  Input,
  Label,
  Margin,
  Padding,
  Paragraph,
  Shade,
  Size,
  Spinner,
  Text,
  TextLink,
  Title,
} from '@drawbotics/react-drylus';
import { useForm } from '@drawbotics/use-form';
import { Link, useAppContext, useController } from '@tokamakjs/react';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { Form, GoogleSSO, MicrosoftSSO, OptionSeparator } from '../../components';
import { AppContext, AuthFlow } from '../../types';
import { createTranslate, translate as t } from '../../utils';
import { LoginController } from './login.controller';

const tt = createTranslate('routes.login');

export const LoginView = () => {
  const appContext = useAppContext<AppContext>();

  const ctrl = useController<LoginController>();

  const [showPassword, setShowpassword] = useState<boolean>(false);

  const loginForm = useForm(
    z.object({
      email: z.string().min(1, t('errors.field_empty')).email(t('errors.invalid_email')),
      password: z.string().min(1, t('errors.field_empty')),
    }),
    {
      defaultValues: {
        email: '',
        password: '',
      },
    },
  );

  const handleSubmit = () => {
    const validationResult = loginForm.validateAll();

    if (validationResult.data) {
      ctrl.login(validationResult.data);
    }
  };

  useEffect(() => {
    const handlePressKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.stopPropagation();

        handleSubmit();
      }
    };

    document.addEventListener('keydown', handlePressKey);

    return () => {
      document.removeEventListener('keydown', handlePressKey);
    };
  }, [loginForm.values]);

  return (
    <DrylusProvider baseColor={appContext.color}>
      <Title>{tt('sign_in')}</Title>
      <Paragraph>
        <Text shade={Shade.LIGHT}>{tt('need_account')}</Text>
        &nbsp;
        <Link
          noWarn
          href={ctrl.searchQueryString ? `../sign-up${ctrl.searchQueryString}` : '../sign-up'}>
          <TextLink>{tt('sign_up_now')}</TextLink>
        </Link>
      </Paragraph>
      <Padding size={{ vertical: Size.SMALL }}>
        <GoogleSSO
          isLoading={ctrl.isHandlingSuccessfulGoogleLogin}
          onSuccess={(r) => ctrl.handleSuccessfulGoogleLogin(r)}
          flow={AuthFlow.SIGN_IN}
        />
      </Padding>
      <Padding size={{ bottom: Size.DEFAULT }}>
        <MicrosoftSSO
          isLoading={ctrl.isHandlingSuccessfulMicrosoftLogin}
          flow={AuthFlow.SIGN_IN}
          locationSearch={location.search}
          onSuccess={(r) => ctrl.handleSuccessfulMicrosoftLogin(r)}
        />
      </Padding>
      <Padding size={{ vertical: Size.SMALL }}>
        <OptionSeparator />
      </Padding>
      <Margin size={{ vertical: Size.DEFAULT }}>
        <Form onSubmit={() => {}}>
          <Margin size={{ top: Size.DEFAULT }}>
            <FormGroup
              label={<Label>{tt('email_address')}</Label>}
              input={
                <Input
                  placeholder={tt('email_placeholder')}
                  name="email"
                  type="email"
                  value={loginForm.get}
                  onChange={loginForm.set}
                  validate={loginForm.validate}
                  error={!!loginForm.getError('email')}
                />
              }
            />
          </Margin>
          <Margin size={{ top: Size.DEFAULT }}>
            <FormGroup
              label={<Label>{tt('password')}</Label>}
              input={
                <Input
                  placeholder={tt('password_placeholder')}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={loginForm.get}
                  onChange={loginForm.set}
                  validate={loginForm.validate}
                  error={!!loginForm.getError('password')}
                  trailing={
                    <Button
                      tabIndex="-1"
                      leading={<Icon name="eye" />}
                      onClick={() => setShowpassword(!showPassword)}
                    />
                  }
                />
              }
            />
          </Margin>
          <Margin size={{ top: Size.EXTRA_SMALL }}>
            <Link noWarn href="../reset-password">
              <TextLink>{tt('forgot_password')}</TextLink>
            </Link>
          </Margin>
          <Margin size={{ top: Size.EXTRA_LARGE }}>
            <Padding size={{ bottom: Size.DEFAULT }}>
              <Button
                color={appContext.color}
                onClick={(e) => {
                  e.preventDefault();

                  handleSubmit();
                }}
                trailing={ctrl.isLoggingIn ? <Spinner size={Size.SMALL} inversed /> : null}
                disabled={ctrl.isLoggingIn}
                size={Size.LARGE}
                fullWidth>
                {tt('sign_in')}
              </Button>
            </Padding>
          </Margin>
        </Form>
      </Margin>
    </DrylusProvider>
  );
};
